import { observer } from "mobx-react-lite";
import React, {useEffect, useState} from "react";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import { Button } from "../../../components/inputs/button/button";
import GlobaleEnvironmentSelector from "./components/globale-environment-selector/globale-environment-selector";
import MigrateGlobalEMerchantsIdsInput from "./components/migrate-global-e-merchants-ids-input/migrate-global-e-merchants-ids-input";
import MigrateGlobalEMerchantsResult from "./components/migrate-global-e-merchants-result/migrate-global-e-merchants-result";
import migrateGlobalEMerchantsPageStore from "./stores/migrate-global-e-merchants-page-store";
import supportedGlobalEEnvsStore from "./stores/supported-global-e-envs-store";
import useValidationGroup from "../../../hooks/validation-group/validation-group-hook";
import Card from "../../../components/layouts/card/card";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import { ConfirmModal } from "../../../components/modal/components/confirm-modal/confirm-modal";
import { BUTTON_VARIANTS } from "../../../components/inputs/button-variants";

const MigrateGlobalePlatformCard: React.FC = observer(() => {
    const vGroup = useValidationGroup();
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const parsedMerchantIdsCount = migrateGlobalEMerchantsPageStore.getMerchantIdsCount();
    const isLoadingInitialData = migrateGlobalEMerchantsPageStore.isLoadingInitialData();
    const isSubmittingMigrationRequest = migrateGlobalEMerchantsPageStore.isSubmittingMigrationRequest();
    const areEnvironmentsLoading = supportedGlobalEEnvsStore.isLoading || !supportedGlobalEEnvsStore.isLoaded;

    useEffect(() => {
        migrateGlobalEMerchantsPageStore.fetchInitialData();
    }, []);

    // HT-710: this action creates Global-E platform stores from the merchant IDs and cannot
    // be rolled back automatically. Require an explicit confirmation before firing the API call.
    const handleMigrateClick = () => {
        vGroup.validate();
        if (!vGroup.isValid()) {
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmMigrate = () => {
        setShowConfirmModal(false);
        void migrateGlobalEMerchantsPageStore.submitMigrationRequest();
    };

    const merchantIds = migrateGlobalEMerchantsPageStore.getMerchantIds();
    const merchantIdsPreview = merchantIds.slice(0, 5).join(", ") + (merchantIds.length > 5 ? `, +${merchantIds.length - 5} more` : "");
    const environment = migrateGlobalEMerchantsPageStore.getEnvironment();

    return (

        <Card isExpanded={true}
            toolbar={{title: 
                <>
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Create and Migrate Global-E Platform Store </FontBody1> 
                    <FontBody1 color={FONT_COLOR.PRIMARY2}>(backend/global-e)</FontBody1>
                </>
            }}  
        >
            <ListLayout>
                <GlobaleEnvironmentSelector 
                    onChange={migrateGlobalEMerchantsPageStore.setEnvironment} 
                    value={migrateGlobalEMerchantsPageStore.getEnvironment()} 
                />

                <MigrateGlobalEMerchantsIdsInput
                    validationGroup={vGroup}
                />

                <Button
                    onClick={handleMigrateClick}
                    text="Start migration"
                    hasLoader={true}
                    isLoading={isSubmittingMigrationRequest}
                    disabled={
                        areEnvironmentsLoading
                        || isLoadingInitialData
                        || isSubmittingMigrationRequest
                        || parsedMerchantIdsCount === 0
                        || !supportedGlobalEEnvsStore.hasSupportedEnvironments
                        || !migrateGlobalEMerchantsPageStore.getEnvironment()
                        || !vGroup.isValid()
                    }
                />

                <MigrateGlobalEMerchantsResult />
            </ListLayout>

            {showConfirmModal && (
                <ConfirmModal
                    title="Create and Migrate Global-E Platform Store"
                    text=""
                    implicationText={
                        <>
                            You are about to migrate <strong>{parsedMerchantIdsCount}</strong> Global-E merchant(s) (IDs: <strong>{merchantIdsPreview}</strong>) on the <strong>{environment}</strong> environment. <mark><em>This action is irreversible</em></mark> and cannot be rolled back automatically.
                        </>
                    }
                    questionText="Confirm?"
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setShowConfirmModal(false),
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: handleConfirmMigrate,
                        variant: BUTTON_VARIANTS.PrimaryAccent,
                    }}
                />
            )}
        </Card>

    );
});

export default MigrateGlobalePlatformCard;
